/**
 * Language switch of the sign-in screen (a port addition: the original was Russian only; in the game the settings
 * screen's account dialog has a button per language: ui/account-dialog.ts).
 *
 * The screens come from the SWF, so there is no asset to place - the row is built at runtime from a label and one
 * clickable field per language, under the buttons of the sign-in screen's first view (each a finger's target of its
 * share of the row: `hit`). Picking a language stores it client-side and restarts the game, because the catalogue has
 * to be in place before the data tables (weapon and perk names) are built.
 */
import { Sprite, TextField, TextFormat } from '@flash';
import { LANGUAGES, language, type Language } from '../runtime/i18n';

const ROW_NAME = '$language_row';
/** Vertical distance of the options from the label. */
const OPTIONS_DY = 22;
const ACTIVE = 0x338000;
/** How far the first and the last option's target reach beyond their word (px). */
const HIT_REACH = 14;
const IDLE = 0x000000;

function field(text: string, x: number, y: number, bold: boolean, size = 12): TextField {
  const tf = new TextField();
  const fmt = new TextFormat('Arial', size, IDLE, bold);
  tf.defaultTextFormat = fmt;
  tf.selectable = false;
  tf.autoSize = 'left';
  tf.x = x;
  tf.y = y;
  tf.text = text;
  return tf;
}

/**
 * Adds the row to a rendered screen (idempotent) and highlights the active language. `pick` is called with the chosen
 * language; it stores the preference and restarts the game. `at`: the row's place.
 */
export function languageRow(
  screen: any,
  pick: (lang: Language) => void,
  at: { x: number; y: number; optionsDy?: number; size?: number; gap?: number; step?: number; hit?: { top: number; bottom: number } },
): Sprite | null {
  if (!screen || typeof screen.addChild !== 'function') return null;
  let row = screen[ROW_NAME] as Sprite | undefined;
  if (!row) {
    row = new Sprite();
    row.name = ROW_NAME;
    // "Язык" is the source string of the port's own label, translated through the catalogue like everything else.
    row.addChild(field('Язык', 0, 0, true));
    // Side by side after the label, or (`step`) one under the other.
    let x = 0;
    let y = at.optionsDy ?? OPTIONS_DY;
    for (const { id, label } of LANGUAGES) {
      const option = field(label, x, y, false, at.size ?? 12);
      option.name = id;
      option.mouseEnabled = true;
      option.buttonMode = true;
      option.addEventListener('click', () => pick(id));
      row.addChild(option);
      if (at.step) y += at.step;
      else x += option.width + (at.gap ?? 14);
    }
    // PORT (touch): each option of a row side by side takes the taps of its share of the band `hit` (the row's
    // coordinates: its top and bottom), split half way between neighbours: a finger's target larger than the word
    // (a transparent rectangle under it that picks the same language).
    if (at.hit && !at.step) {
      const opts = LANGUAGES.map(({ id }) => row!.getChildByName(id) as TextField);
      opts.forEach((o, i) => {
        const left = i === 0 ? o.x - HIT_REACH : (opts[i - 1].x + opts[i - 1].width + o.x) / 2;
        const right = i === opts.length - 1 ? o.x + o.width + HIT_REACH : (o.x + o.width + opts[i + 1].x) / 2;
        const hit = new Sprite();
        hit.name = `$hit_${o.name}`;
        hit.graphics.beginFill(0, 0);
        hit.graphics.drawRect(left, at.hit!.top, right - left, at.hit!.bottom - at.hit!.top);
        hit.graphics.endFill();
        hit.buttonMode = true;
        const id = o.name as Language;
        hit.addEventListener('click', () => pick(id));
        // (under the words: a tap on a word reaches the word itself)
        row!.addChildAt(hit, row!.getChildIndex(opts[0]));
      });
    }
    row.x = at.x;
    row.y = at.y;
    screen.addChild(row);
    screen[ROW_NAME] = row;
  }
  const active = language();
  for (let i = 0; i < row.numChildren; i++) {
    const child = row.getChildAt(i) as TextField;
    if (!child.name || child.name === ROW_NAME || !(child instanceof TextField)) continue;
    child.textColor = child.name === active ? ACTIVE : IDLE;
  }
  return row;
}

/** Width of the options line of a row (for centring it). */
export function languageRowWidth(row: Sprite): number {
  let right = 0;
  for (let i = 0; i < row.numChildren; i++) {
    const child = row.getChildAt(i) as TextField;
    if (child instanceof TextField) right = Math.max(right, child.x + child.width);
  }
  return right;
}
